"use client";

import React, { useState } from "react";
import { cn, copyText } from "utils";

import NextImage from "../../NextImage";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { TokenInfo as TokenInfoType } from "./types";
import { ospThemes } from "@ospweb/sdk-ui";

interface TokenInfoProps {
  token: TokenInfoType;
}

// 格式化地址显示（前5位+省略号+后3位）
const formatAddress = (address: string): string => {
  if (address.length <= 8) {
    return address;
  }
  return `${address.slice(0, 5)}...${address.slice(-3)}`;
};

export const TokenInfo: React.FC<TokenInfoProps> = ({ token }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyAddress = async () => {
    if (!token.address) {
      return;
    }

    const success = await copyText(token.address);
    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    }
  };

  const handleSocialClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // 社交媒体图标映射
  const getSocialIcon = (key: string): string => {
    const iconMap: Record<string, string> = {
      website: "globe",
      twitter: "X-logo",
      telegram: "Telegram",
      discord: "Discord-D",
      linkedin: "LinkedIn-L",
      facebook: "facebook-f",
      reddit: "Reddit",
      slack: "slack",
      github: "github",
      medium: "Medium",
    };
    return iconMap[key] || "globe";
  };

  const renderSocials = () => {
    if (!token.socials || token.socials.length === 0) {
      return null;
    }

    return (
      <div className={cn("inline-flex items-center gap-1")}>
        {token.socials.map((social) => {
          const IconKey = getSocialIcon(social.key);
          return IconKey !== "Reddit" ? (
            <button
              aria-label="Token Info"
              data-openreplay-label="chatbox_tokeninfo_click"
              id="btn-token-info"
              key={social.key}
              onClick={() => handleSocialClick(social.url)}
              className={cn(
                "relative w-5 h-5 rounded-full aspect-square",
                "hover:opacity-80 transition-opacity cursor-pointer",
                "flex items-center justify-center overflow-hidden",
              )}
            >
              <OspIcons
                name={getSocialIcon(social.key)}
                size={12}
                color={ospThemes.colors.content_tertiary}
              />
            </button>
          ) : (
            //  todo icon 有问题
            <NextImage
              src="/assets/images/Reddit.svg"
              className="w-3 h-3 flex-shrink-0"
              key={social.key}
            />
          );
        })}
      </div>
    );
  };
  return (
    <div className={cn("inline-flex items-start gap-3")}>
      <div className={cn("inline-flex items-center gap-2 rounded-4")}>
        {/* 头像 */}
        <div className={cn("relative w-11 h-11 aspect-square")}>
          <NextImage
            src={token.avatarSrc}
            alt={`${token.name} logo`}
            width={44}
            height={44}
            className={cn("w-full h-full object-cover rounded-full")}
          />
        </div>

        {/* 公司名和代码 */}
        <div className={cn("inline-flex flex-col items-start justify-center")}>
          <div className={cn("inline-flex items-center gap-0.5")}>
            <div
              className={cn(
                "body_medium_l text-content_primary whitespace-nowrap",
              )}
            >
              {token.symbol}
            </div>
            <div
              className={cn("body_m text-content_tertiary whitespace-nowrap")}
            >
              {token.name}
            </div>
          </div>

          {/* 地址和社交媒体图标 */}
          <div className={cn("inline-flex items-center gap-1")}>
            {/* 地址显示 */}
            {token.address && (
              <div className={cn("inline-flex items-center gap-1")}>
                <div
                  className={cn(
                    "body_m text-content_tertiary whitespace-nowrap",
                  )}
                >
                  {formatAddress(token.address)}
                </div>
                <button
                  data-openreplay-label="chatbox_action_copy"
                  id="btn-action-tokenpopover-tokeninfo-2"
                  onClick={handleCopyAddress}
                  className={cn("hover:opacity-80 transition-opacity")}
                  aria-label="复制地址"
                >
                  <OspIcons
                    name={isCopied ? "check" : "ai-copy"}
                    size={12}
                    color={
                      isCopied
                        ? ospThemes.colors.content_accent1_primary
                        : ospThemes.colors.content_tertiary
                    }
                  />
                </button>
              </div>
            )}

            {/* 社交媒体图标 */}
            {renderSocials()}
          </div>
        </div>
      </div>
    </div>
  );
};
